import { useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import './Photo.css';

interface PhotoProps {
  /** Telegram photo URL (time-limited, signed). */
  src: string | null;
  /**
   * User's display name — used for the fallback initials and alt text.
   * Optional at the type level because older shapes of the games list
   * payload (and other defensive paths) can briefly hand us an
   * undefined name before the cache catches up.
   */
  name?: string | null;
  /** Optional size in px (default 40). */
  size?: number;
  /** Visual variant. */
  variant?: 'circle' | 'rounded';
  /** Extra class name on the wrapper. */
  className?: string;
  topLeftBadge?: ReactNode;
  bottomRightBadge?: ReactNode;
}

/**
 * Renders a Telegram profile photo, falling back to initials on the brand
 * gradient if the photo is missing or fails to load. The gradient rotates
 * through 4 palettes so consecutive users don't all look the same.
 */
export function Photo({
  src,
  name,
  size = 40,
  variant = 'circle',
  className,
  topLeftBadge,
  bottomRightBadge,
}: PhotoProps) {
  const [error, setError] = useState(false);
  const safeName = name ?? '';
  const initials = safeName
    ? safeName
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';

  // Stable gradient index derived from name length, so the same user
  // always gets the same colour without needing a hash function.
  // `% 4` of a non-negative integer is always safe; we coerce `length`
  // through `|| 0` so a missing/empty name still picks a stable palette.
  const gradientIndex = (safeName.length || 1) % 4;

  const wrapperStyle = {
    width: size,
    height: size,
    fontSize: Math.max(11, Math.round(size * 0.4)),
  };

  const showPhoto = src && !error;

  return (
    // Outer wrapper provides a positioning context for badges that need
    // to overflow the photo's rounded corners (e.g. the skill badge in
    // the bottom-right). The inner `.photo` keeps `overflow: hidden` so
    // the gradient/image stay rounded, but the wrapper's corners are
    // unconstrained — the badge can poke out by a couple of pixels
    // without being clipped.
    <span
      className={`photoWrap${className ? ` ${className}` : ''}`}
      style={wrapperStyle}
      aria-label={safeName ? `${safeName}'s avatar` : 'avatar'}
      role="img"
    >
      <span
        className={`photo photo-${variant}${showPhoto ? ' photo-has-image' : ''}`}
      >
        {showPhoto ? (
          <img
            src={src}
            alt=""
            onError={() => setError(true)}
            loading="lazy"
            decoding="async"
          />
        ) : initials ? (
          <span className={`photo-initials photo-gradient-${gradientIndex}`}>{initials}</span>
        ) : (
          <span className={`photo-fallback photo-gradient-${gradientIndex}`}>
            <Icon name="user-account" size={Math.round(size * 0.55)} />
          </span>
        )}
      </span>
      {topLeftBadge ? <span className="photo-badge photo-badge-topLeft">{topLeftBadge}</span> : null}
      {bottomRightBadge ? <span className="photo-badge photo-badge-bottomRight">{bottomRightBadge}</span> : null}
    </span>
  );
}
